import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useBlocker } from "react-router-dom";
import { semesterReviewService, authService } from "../../services";
import LoadingSpinner from "../common/LoadingSpinner";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { useVisibility } from "../../contexts/VisibilityContext";
import LanguageSwitcher from "../common/LanguageSwitcher";
import LeaveProtectionModal from "../common/LeaveProtectionModal";

const LearnerSemesterReviewsPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const { canAccess } = useVisibility();
  const fileInputRef = useRef(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [error, setError] = useState(null);
  const [newReview, setNewReview] = useState({
    title: "",
    templateCode: "KOMPETENZEN",
  });

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  
  const [semesterOptions, setSemesterOptions] = useState([]);
  const [selectedSemesterCode, setSelectedSemesterCode] = useState("");

  // Only INFORMATIK learners may create reviews; others can still view them.
  // The profession-based visibility setting can further disable creation.
  const canCreateReview = useMemo(() => {
    const profession = user?.professionType;
    const professionAllowed =
      profession === "INFORMATIK" || profession === "INFORMATIKER";
    return professionAllowed && canAccess("CREATE_SEMESTER_REVIEWS");
  }, [user, canAccess]);

  // Determine dirty state (unsaved changes in dialogs)
  const isCreateDirty = useMemo(() => {
    return showCreateDialog && newReview.title.trim() !== "";
  }, [showCreateDialog, newReview.title]);

  const isEditDirty = useMemo(() => {
    if (!showEditDialog || !editingReview) return false;
    return editTitle.trim() !== editingReview.title;
  }, [showEditDialog, editingReview, editTitle]);

  const isDirty = isCreateDirty || isEditDirty;

  // Blocker for unsaved changes
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
        isDirty && currentLocation.pathname !== nextLocation.pathname
  );

  const handleSaveAndLeave = async () => {
      if (isCreateDirty) {
          await handleCreateReview();
      } else if (isEditDirty) {
          await handleUpdateReview();
      }
  };


  const loadReviews = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await semesterReviewService.getMyReviews();
      setReviews(data || []);
      
      // Load options too
      try {
         const currentUser = authService.getCurrentUser();
         const options = await semesterReviewService.getSemesterOptions(currentUser?.id);
         setSemesterOptions(options);

         if (options && options.length > 0) {
            const now = new Date();
            const month = now.getMonth();
            const year = now.getFullYear();
            
            let targetCode = "";
            if (month === 0 || month === 1) { // Jan-Feb
                targetCode = `HS${(year - 1) % 100}`;
            } else if (month >= 2 && month < 7) { // Mar-Jul
                targetCode = `FS${year % 100}`;
            } else { // Aug-Dec
                targetCode = `HS${year % 100}`;
            }
   
            const found = options.find(o => o.semesterCode === targetCode);
            if (found) {
               setSelectedSemesterCode(targetCode);
            } else {
               setSelectedSemesterCode(options[options.length - 1].semesterCode);
            }
         }
      } catch (err) {
         console.error("Failed to load options", err);
      }
      
    } catch (error) {
      console.error("Error loading reviews:", error);
      setError("Could not load your semester reviews.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const generateDefaultTitle = (templateCode, semCode = selectedSemesterCode, lang = language) => {
    const semOption = semesterOptions.find(opt => opt.semesterCode === semCode);
    const semesterLabel = semOption ? semOption.semesterCode : ""; // HS25

    const currentUser = authService.getCurrentUser();
    const learnerName = `${currentUser.firstName} ${currentUser.lastName}`;
    
    // Legacy support if options not loaded?
    // But we should use backend label if possible.
    
    if (templateCode === "KOMPETENZEN") {
      return lang === "de"
        ? `${learnerName} Bildungsbericht ${semesterLabel}_Lernender`
        : `${learnerName} Educational Report ${semesterLabel}_Learner`;
    }
    return lang === "de"
      ? `${learnerName} Semestergespräch ${semesterLabel}_Lernender`
      : `${learnerName} Semester Review ${semesterLabel}_Learner`;
  };


  const handleReviewClick = (reviewId) => {
    navigate(`/semester-review/${reviewId}`);
  };

  const handleCreateReview = async () => {
    if (!newReview.title.trim()) {
      setError(
        language === "de"
          ? "Bitte geben Sie einen Titel ein."
          : "Please fill in the title for your review.",
      );
      return;
    }

    const currentUser = authService.getCurrentUser();
    if (!currentUser || !currentUser.id) {
      setError(
        language === "de"
          ? "Benutzersitzung ungültig. Bitte melden Sie sich erneut an."
          : "User session invalid. Please log in again.",
      );
      return;
    }

    setError(null);

    try {
      setLoading(true);
      
      const reviewData = {
        learnerId: currentUser.id,
        templateCode: newReview.templateCode,
        title: newReview.title.trim(),
        status: "DRAFT",
      };

      const created = await semesterReviewService.createReview(reviewData);
      setShowCreateDialog(false);
      setNewReview({ title: "", templateCode: "KOMPETENZEN" });

      navigate(`/semester-review/${created.id}`);
    } catch (error) {
      console.error("Error creating review:", error);
      if (error.response && error.response.data) {
        console.error("Server error details:", error.response.data);
      }
      
      let serverMessage = "Unknown error";
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          serverMessage = error.response.data;
        } else if (typeof error.response.data === 'object') {
          serverMessage = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        serverMessage = error.message;
      }
        
      setError(
        (language === "de"
          ? "Error creating review: "
          : "Error creating review: ") + serverMessage,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (review, e) => {
    e.stopPropagation();
    setEditingReview(review);
    setEditTitle(review.title);
    setShowEditDialog(true);
  };

  const handleUpdateReview = async () => {
    if (!editingReview || !editTitle.trim()) return;

    try {
      setLoading(true);
      await semesterReviewService.updateReviewDetails(editingReview.id, {
        title: editTitle.trim(),
      });
      setShowEditDialog(false);
      setEditingReview(null);
      setEditTitle("");
      loadReviews();
    } catch (error) {
      console.error("Error updating review:", error);
      setError(
        (language === "de"
          ? "Error updating review: "
          : "Error updating review: ") + (error.message || "Unknown error"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (review, e) => {
    e.stopPropagation();
    setReviewToDelete(review);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!reviewToDelete) return;

    try {
      await semesterReviewService.deleteReview(reviewToDelete.id);
      setError(null);
      setShowDeleteDialog(false);
      setReviewToDelete(null);
      loadReviews();
    } catch (error) {
      console.error("Error deleting review:", error);
      setError(
        "Error deleting review: " + (error.message || "Unknown error")
      );
      setShowDeleteDialog(false);
      setReviewToDelete(null);
    }
  };

  const handleImportPdf = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const currentUser = authService.getCurrentUser();
    if (!currentUser || !currentUser.id) {
       setError(
        language === "de"
          ? "Benutzersitzung ungültig. Bitte melden Sie sich erneut an."
          : "User session invalid. Please log in again.",
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const importedReview =
        await semesterReviewService.importReviewFromPdf(file, currentUser.id);
      navigate(`/semester-review/${importedReview.id}`);
    } catch (error) {
      console.error("Error importing PDF:", error);
      const serverMessage = error.response?.data && typeof error.response.data === 'string' 
        ? error.response.data 
        : (error.message || "Unknown error");

      setError(
        "Error importing PDF: "+ serverMessage,
      );
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      DRAFT: {
        pill: "bg-gray-100 text-gray-600 border-gray-200",
        dot: "bg-gray-400",
        label: "Draft",
      },
      IN_REVIEW: {
        pill: "bg-blue-50 text-blue-700 border-blue-200",
        dot: "bg-blue-500",
        label: "In Review",
      },
      COMPLETED: {
        pill: "bg-green-50 text-green-700 border-green-200",
        dot: "bg-green-500",
        label: "Completed",
      },
    };

    const cfg = config[status] || {
      pill: "bg-gray-100 text-gray-600 border-gray-200",
      dot: "bg-gray-400",
      label: status,
    };

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.pill}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
        {cfg.label}
      </span>
    );
  };

  const TEMPLATE_ICONS = {
    KOMPETENZEN: "fa-clipboard-check",
    BEWERTUNG_DER_AUSBILDUNG: "fa-comments",
    BB_LEISTUNGSBEURTEILUNG: "fa-chart-line",
  };

  // The title carries the semester ("... HS26_Lernender"); surfacing it as a
  // chip makes the list scannable without reading every title.
  const getSemesterChip = (review) => {
    if (review.semesterLabel) return review.semesterLabel;
    const match = `${review.title || ""}`.match(/\b(HS|FS)\s?\d{2,4}\b/i);
    return match ? match[0].toUpperCase() : null;
  };

  const formatDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? null
      : date.toLocaleDateString("de-CH");
  };

  const getTemplateLabel = (templateCode) => {
    if (language === "de") {
      const labels = {
        KOMPETENZEN: "Bildungsbericht",
        BEWERTUNG_DER_AUSBILDUNG: "Bewertung der Ausbildung",
        BB_LEISTUNGSBEURTEILUNG: "Leistungsbeurteilung",
      };
      return labels[templateCode] || templateCode;
    } else {
      const labels = {
        KOMPETENZEN: "Educational Report",
        BEWERTUNG_DER_AUSBILDUNG: "Evaluation of Training",
        BB_LEISTUNGSBEURTEILUNG: "Performance Review",
      };
      return labels[templateCode] || templateCode;
    }
  };

  if (loading && !showCreateDialog) {
    return (
      <LoadingSpinner
        message={language === "de" ? "Lade Gespräche..." : "Loading reviews..."}
      />
    );
  }

  return (
    <div className="min-h-screen bg-green-cloud">
      <LeaveProtectionModal blocker={blocker} onSaveAndLeave={handleSaveAndLeave} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="card-brand-elevated p-6 md:p-8 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-grey-dusk flex items-center mb-2">
                  <i className="fas fa-clipboard-check text-sunrise-red mr-4"></i>
                  My Semester Reviews
                </h1>
                <p className="text-sm md:text-base text-slate-cloud">
                  See all your semester reviews in one place. Click a card to
                  open the detailed review.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 justify-start lg:justify-end">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImportPdf}
                  accept=".pdf"
                  className="hidden"
                />
                {canCreateReview && (
                  <button
                    onClick={() => {
                      const defaultTemplate = "KOMPETENZEN";
                      setNewReview({
                        title: generateDefaultTitle(defaultTemplate),
                        templateCode: defaultTemplate,
                      });
                      setShowCreateDialog(true);
                      setError(null);
                    }}
                    className="bg-sunrise-red hover:bg-sunrise-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center text-sm shadow-sm"
                  >
                    <i className="fas fa-plus mr-2"></i>
                    <span>Create Review</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Global error (z. B. Laden) */}
        {error && !showCreateDialog && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-3 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="fas fa-exclamation-circle mr-3"></i>
                <span>{error}</span>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-500 hover:text-red-700"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        {reviews.length === 0 ? (
          <div className="card-brand p-10 md:p-12 text-center">
            <div className="w-20 h-20 bg-sunrise-red/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <i className="fas fa-inbox text-3xl text-sunrise-red"></i>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-grey-dusk mb-2">
              No reviews yet
            </h2>
            <p className="text-sm text-slate-cloud mb-5">
              You have not received or created any semester reviews yet.
            </p>
            {canCreateReview && (
              <button
                onClick={() => {
                  const defaultTemplate = "BEWERTUNG_DER_AUSBILDUNG";
                  setNewReview({
                    title: generateDefaultTitle(defaultTemplate),
                    templateCode: defaultTemplate,
                  });
                  setShowCreateDialog(true);
                  setError(null);
                }}
                className="bg-sunrise-red hover:bg-sunrise-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors inline-flex items-center gap-2 text-sm shadow-sm"
              >
                <i className="fas fa-plus"></i>
                <span>Create your first review</span>
              </button>
            )}
          </div>
        ) : (
          <div className="card-brand overflow-hidden">
            <div className="px-5 py-4 border-b border-pale-dawn bg-gradient-to-r from-ice-white to-pale-dawn/30 flex items-center justify-between gap-3">
              <h2 className="text-lg font-bold text-grey-dusk flex items-center">
                <i className="fas fa-list-alt text-sunrise-red mr-3"></i>
                Your Reviews
              </h2>
              <span className="px-2.5 py-1 rounded-full bg-white border border-pale-dawn text-xs font-semibold text-grey-dusk">
                {reviews.length}
              </span>
            </div>

            <ul className="divide-y divide-pale-dawn">
              {reviews.map((review) => {
                const semesterChip = getSemesterChip(review);
                const created = formatDate(review.createdAt);
                const completed = formatDate(review.completedAt);
                const isDraft = review.status === "DRAFT";
                const unread =
                  review.status === "COMPLETED" &&
                  review.isReadByLearner === false;

                return (
                  <li key={review.id}>
                    {/* A div, not a button: the draft actions below are buttons
                        and must not be nested inside one. */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => handleReviewClick(review.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleReviewClick(review.id);
                        }
                      }}
                      className="group px-5 py-4 flex items-start gap-3 cursor-pointer hover:bg-ice-white/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-sunrise-red/40 transition-colors"
                    >
                      <span className="mt-0.5 w-10 h-10 shrink-0 rounded-xl bg-sunrise-red/10 text-sunrise-red flex items-center justify-center">
                        <i
                          className={`fas ${
                            TEMPLATE_ICONS[review.templateCode] ||
                            "fa-file-lines"
                          }`}
                        ></i>
                      </span>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <h3 className="font-semibold text-grey-dusk leading-snug truncate group-hover:text-sunrise-red transition-colors">
                            {review.title ||
                              (language === "de"
                                ? "Unbenanntes Gespräch"
                                : "Untitled Review")}
                          </h3>

                          <div className="flex items-center gap-1 shrink-0">
                            {getStatusBadge(review.status)}
                            {isDraft && (
                              <>
                                <button
                                  onClick={(e) => handleEditClick(review, e)}
                                  className="w-7 h-7 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center"
                                  title="Edit title"
                                  aria-label="Edit title"
                                >
                                  <i className="fas fa-edit text-xs"></i>
                                </button>
                                <button
                                  onClick={(e) => handleDeleteClick(review, e)}
                                  className="w-7 h-7 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center"
                                  title={
                                    language === "de"
                                      ? "Löschen"
                                      : "Delete draft"
                                  }
                                  aria-label={
                                    language === "de"
                                      ? "Löschen"
                                      : "Delete draft"
                                  }
                                >
                                  <i className="fas fa-trash-alt text-xs"></i>
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                          <span className="text-gray-600">
                            {getTemplateLabel(review.templateCode)}
                          </span>
                          {semesterChip && (
                            <>
                              <span className="text-gray-300">·</span>
                              <span className="px-1.5 py-0.5 rounded bg-pale-dawn/60 text-grey-dusk font-medium">
                                {semesterChip}
                              </span>
                            </>
                          )}
                          {unread && (
                            <>
                              <span className="text-gray-300">·</span>
                              <span className="px-1.5 py-0.5 rounded bg-sunrise-red/10 text-sunrise-red font-semibold">
                                New
                              </span>
                            </>
                          )}
                        </div>

                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-400">
                          {created && (
                            <span>
                              <i className="fas fa-calendar-alt mr-1.5"></i>
                              Created {created}
                            </span>
                          )}
                          {review.authorName && (
                            <span>
                              <i className="fas fa-user mr-1.5"></i>
                              {review.authorName}
                            </span>
                          )}
                          {completed && (
                            <span className="text-green-600">
                              <i className="fas fa-check-circle mr-1.5"></i>
                              Completed {completed}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* Edit Review Dialog */}
      {showEditDialog && editingReview && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="card-brand max-w-lg w-full p-6 md:p-8">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <i className="fas fa-edit text-blue-600 text-xl"></i>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-grey-dusk">
                Edit Review
              </h2>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-grey-dusk mb-2">
                Title
              </label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-pale-dawn focus:ring-2 focus:ring-sunrise-red focus:border-sunrise-red transition-all"
                placeholder="Review title"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  setShowEditDialog(false);
                  setEditingReview(null);
                }}
                className="flex-1 px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateReview}
                className="flex-1 px-6 py-3 bg-sunrise-red text-white rounded-xl hover:bg-sunrise-600 transition-all shadow-md hover:shadow-lg font-medium inline-flex items-center justify-center"
              >
                <i className="fas fa-check mr-2"></i>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && reviewToDelete && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="card-brand max-w-md w-full p-6 md:p-8">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <i className="fas fa-exclamation-triangle text-red-600 text-xl"></i>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-grey-dusk">
                Delete draft?
              </h2>
            </div>
            <p className="text-sm md:text-base text-gray-600 mb-6">
              Do you really want to delete the review{" "}
              <strong>"{reviewToDelete.title}"</strong>? This action cannot be
              undone.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowDeleteDialog(false)}
                className="flex-1 px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-6 py-3 bg-sunrise-red text-white rounded-xl hover:bg-sunrise-600 transition-all shadow-md hover:shadow-lg font-medium inline-flex items-center justify-center"
              >
                <i className="fas fa-trash mr-2"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Review Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="card-brand max-w-lg w-full relative">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => {
                  setShowCreateDialog(false);
                  setError(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times text-lg"></i>
              </button>
            </div>

            <div className="p-6 md:p-8">
              <div className="flex justify-between items-start mb-2">
                <h2 className="text-xl md:text-2xl font-bold text-grey-dusk flex items-center">
                  <span className="w-10 h-10 rounded-xl bg-sunrise-red/10 flex items-center justify-center mr-3">
                    <i className="fas fa-plus text-sunrise-red"></i>
                  </span>
                  Create New Review
                </h2>
                <div className="mt-1">
                  <LanguageSwitcher />
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Start a new self-assessment or review for this semester.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-grey-dusk mb-2">
                    Template
                  </label>
                  <select
                    value={newReview.templateCode}
                    onChange={(e) => {
                      const newTemplate = e.target.value;
                      setNewReview({
                        templateCode: newTemplate,
                        title: generateDefaultTitle(newTemplate, selectedSemesterCode),
                      });
                    }}
                    className="input-brand w-full"
                  >
                    <option value="KOMPETENZEN">
                      {language === "de"
                        ? "Bildungsbericht"
                        : "Educational Report"}
                    </option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-dusk mb-2">
                    Semester
                  </label>
                  <select
                    value={selectedSemesterCode}
                    onChange={(e) => {
                      const newCode = e.target.value;
                      setSelectedSemesterCode(newCode);
                      setNewReview((prev) => ({
                        ...prev,
                        title: generateDefaultTitle(prev.templateCode, newCode),
                      }));
                    }}
                    className="input-brand w-full"
                  >
                    {semesterOptions.map((opt) => (
                      <option key={opt.semesterCode} value={opt.semesterCode}>
                        {opt.displayShort}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-dusk mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={newReview.title}
                    onChange={(e) =>
                      setNewReview({ ...newReview, title: e.target.value })
                    }
                    className="input-brand w-full"
                    placeholder={
                      language === "de"
                        ? "z.B. Semestergespräch HS25"
                        : "e.g. Semester 1 Review 2025"
                    }
                  />
                  <div className="flex justify-end mt-1">
                    <button
                      type="button"
                      onClick={() =>
                        setNewReview({
                          ...newReview,
                          title: generateDefaultTitle(newReview.templateCode),
                        })
                      }
                      className="text-xs text-sunrise-red hover:underline"
                    >
                      Regenerate Title
                    </button>
                  </div>
                </div>

                <div className="text-xs md:text-sm text-gray-600 bg-blue-50 p-3 rounded-lg flex items-start">
                  <i className="fas fa-info-circle mr-2 mt-0.5 text-blue-600"></i>
                  <p>
                    Self-Assessment: Use this to share your perspective on your
                    training, company and trainer. This can help prepare
                    semester talks and educational reports.
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
                    <i className="fas fa-exclamation-circle mr-2"></i>
                    {error}
                  </div>
                )}
              </div>

              <div className="flex flex-row gap-3 mt-6">
                {/* Cancel */}
                <button
                  onClick={() => {
                    setShowCreateDialog(false);
                    setError(null);
                  }}
                  disabled={loading}
                  className="flex-1 px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 
               transition-colors text-sm font-medium flex items-center justify-center"
                >
                  Cancel
                </button>

                {/* Create */}
                <button
                  onClick={handleCreateReview}
                  disabled={loading}
                  className="flex-1 px-6 py-3 rounded-lg bg-sunrise-red hover:bg-sunrise-600 text-white 
               transition-colors text-sm font-semibold flex items-center justify-center"
                >
                  {loading ? "Creating..." : "Create review"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearnerSemesterReviewsPage;
